/**
 * The localStorage keys that sign-out keeps: view preferences, the idle
 * clock's timestamp and the board arrangements that belong to a named
 * user. None holds client data. Every writer imports its key from here, so
 * this list and the writers cannot drift apart.
 *
 * The default is to clear: a key not named here is removed at sign-out
 * (src/lib/signedInStorage.ts) until someone names it as kept. Note drafts
 * are kept too, by their own rule (src/lib/draftAutosave.ts `isDraftKey`),
 * not by this list.
 */

import { ACTIVITY_KEY } from '@/lib/idleSignOut';

export const VITALS_OVERVIEW_KEY = 'kith-vitals-overview-open';
export const HUB_MY_TASKS_WINDOW_KEY = 'peace.hub.mytasks.window';
export const PEOPLE_DRAWER_WIDTH_KEY = 'peaceos.people.drawerWidth';
export const WORKSPACE_DRAWER_WIDTH_KEY = 'peaceos.workspace.drawerWidth';
export const WORKSPACE_DRAWER_ATTACHMENTS_KEY = 'peaceos.workspace.drawer.attachments';
export const WORKSPACE_DRAWER_CLIENT_DOCUMENTS_KEY = 'peaceos.workspace.drawer.clientDocuments';
export const WORKSPACE_DRAWER_INTAKE_DETAILS_KEY = 'peaceos.workspace.drawer.intakeDetails';
export const RECENT_TOOLS_KEY = 'peace-os.recent-tools.v1';

/** A record card's open or closed state: this prefix, then the card's test
 *  id (`card-` and lower-case words joined by hyphens). */
export const CARD_OPEN_KEY_PREFIX = 'kith-card-open:';

/** The board's per-user lane order and collapsed lanes: these prefixes,
 *  then `<tenant slug>.<user id>` (and `.<board>` for the collapsed lanes). */
export const LANE_SORT_KEY_PREFIX = 'peaceos.workspace.laneSort.';
export const LANE_COLLAPSE_KEY_PREFIX = 'peaceos.workspace.laneCollapse.';

const KEPT_EXACT: ReadonlySet<string> = new Set([
  VITALS_OVERVIEW_KEY,
  HUB_MY_TASKS_WINDOW_KEY,
  PEOPLE_DRAWER_WIDTH_KEY,
  WORKSPACE_DRAWER_WIDTH_KEY,
  WORKSPACE_DRAWER_ATTACHMENTS_KEY,
  WORKSPACE_DRAWER_CLIENT_DOCUMENTS_KEY,
  WORKSPACE_DRAWER_INTAKE_DETAILS_KEY,
  RECENT_TOOLS_KEY,
  ACTIVITY_KEY,
]);

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A literal card test id only: a key built from data, which could carry a
 *  record id, does not match. */
const CARD_OPEN_KEY = new RegExp(`^${escapeRegExp(CARD_OPEN_KEY_PREFIX)}card-[a-z]+(-[a-z]+)*$`);

/** Slug and user segments non-empty: a key the board wrote before it knew
 *  the user is nobody's arrangement and is not kept. */
const LANE_SORT_KEY = new RegExp(`^${escapeRegExp(LANE_SORT_KEY_PREFIX)}[^.]+\\.[^.]+$`);
const LANE_COLLAPSE_KEY = new RegExp(`^${escapeRegExp(LANE_COLLAPSE_KEY_PREFIX)}[^.]+\\.[^.]+\\..+$`);

export function isKeptAcrossSignOut(key: string): boolean {
  return (
    KEPT_EXACT.has(key) || CARD_OPEN_KEY.test(key) || LANE_SORT_KEY.test(key) || LANE_COLLAPSE_KEY.test(key)
  );
}
